import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom"; // Ensure you have react-router-dom installed
import "./appointmentModal.css"; // Optional: For any custom styling
import { useAuth } from "../../../hooks/useAuth";
import PracticeIcon from "../../searchResults/assets/practice.png";
import LocationIcon from "../../searchResults/assets/location.png";
import FirmIcon from "../../searchResults/assets/firm.png";
import StarFill from "../assets/starFill.png";
import { getPracticeAreaLabel } from "../../../helpers/stringHelper";

const AppointmentModal = ({
  lawyer, // Pass the lawyer's ID directly
  consultation,
  selectedDay,
  onClose,
}) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const { isAuthenticated, userType } = useAuth();

  // Snackbar state for user feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Local availability state to manage appended data
  const [availability, setAvailability] = useState([]);

  // State to manage pagination or date ranges
  const [currentEndDate, setCurrentEndDate] = useState(null); // Keeps track of the last date fetched

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize availability when the modal mounts
  useEffect(() => {
    if (selectedDay && lawyer) {
      fetchAvailability(selectedDay.date, 14); // Fetch the initial 2 weeks starting from selected day
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, lawyer]);

  // Function to handle slot selection and navigate to checkout
  const handleSlotSelect = async (slot, day) => {
    const query = new URLSearchParams({
      lawyerId: lawyer.user_id,
      lawyerName: lawyer.name,
      practiceAreas: lawyer.practice_areas,
      priceId: consultation.stripe_price_id,
      productId: consultation.stripe_product_id,
      accountId: consultation.stripe_connect_id,
      date: day.date,
      startTime: slot.startTime,
      endTime: slot.endTime,
      price: consultation.appointment_price / 100,
      firmId: lawyer.firm_id,
      appointmentTypeId: consultation.id,
      appointmentName: consultation.appointment_name,
      lawyerPhoto: lawyer.profile_photo_url,
      duration: consultation.appointment_duration,
    }).toString();

    console.log("Authenticated state   ", isAuthenticated);
    console.log("User type   ", userType);
    if (!isAuthenticated || userType !== "client") {
      navigate(`/createuser/auth-appointment?${query}`); // Or another appropriate route for sign-in
      onClose();
      return;
    }

    navigate(`/client/intake-form?${query}`);
    onClose();
  };

  // Format an "HH:mm:ss" (from UTC) into local "h:mm AM/PM"
  const formatTime = (timeStr) => {
    // Build a Date in UTC by appending 'Z'
    const date = new Date(`1970-01-01T${timeStr}Z`);
    // toLocaleTimeString will convert it to the user's local zone
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  // Helper function to split a slot into 1‑hour intervals, in *local* time
  // function splitSlotIntoHours(startTime, endTime) {
  //   const slots = [];
  //   // Parse HH:mm:ss as UTC by appending 'Z'
  //   let cursor = new Date(`1970-01-01T${startTime}Z`);
  //   const boundary = new Date(`1970-01-01T${endTime}Z`);

  //   while (cursor < boundary) {
  //     const next = new Date(cursor.getTime() + 60 * 60 * 1000);
  //     slots.push({
  //       startTime: cursor.toISOString().slice(11, 19), // keep "HH:MM:SS"
  //       endTime: next.toISOString().slice(11, 19),
  //     });
  //     cursor = next;
  //   }

  //   return slots;
  // }

  function splitSlotByDuration(startTime, endTime, durationMinutes) {
    const slots = [];
    let cursor = new Date(`1970-01-01T${startTime}Z`);
    const boundary = new Date(`1970-01-01T${endTime}Z`);
    const step = durationMinutes * 60 * 1000;

    while (cursor.getTime() + step <= boundary.getTime()) {
      const next = new Date(cursor.getTime() + step);
      slots.push({
        startTime: cursor.toISOString().slice(11, 19),
        endTime: next.toISOString().slice(11, 19),
      });
      cursor = next;
    }

    return slots;
  }

  // Function to fetch availability data
  const fetchAvailability = useCallback(
    async (startDateStr, daysToFetch) => {
      setLoading(true);
      setError(null);
      try {
        const startDate = new Date(startDateStr);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + daysToFetch - 1); // Inclusive

        const formattedStartDate = startDate.toISOString().split("T")[0];
        const formattedEndDate = endDate.toISOString().split("T")[0];

        const response = await fetch(
          `/api/attorney/availability?lawyer_id=${lawyer.user_id}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
        );
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        setAvailability((prev) => [...prev, ...(data || [])]);

        // Update the currentEndDate for the next fetch
        setCurrentEndDate(endDate);
      } catch (err) {
        console.error("Failed to fetch availability:", err);
        setError("Failed to load availability.");
      } finally {
        setLoading(false);
      }
    },
    [lawyer]
  );

  // Function to load more appointments (next 2 weeks)
  const loadMoreAppointments = async () => {
    if (!currentEndDate) return; // Safety check

    const nextStartDate = new Date(currentEndDate);
    nextStartDate.setDate(nextStartDate.getDate() + 1); // Day after the last fetched date

    await fetchAvailability(nextStartDate.toISOString().split("T")[0], 14); // Fetch next 2 weeks
  };

  // Filter availability from the selected date onward
  const filteredAvailability = availability
    .filter((day) => {
      const selectedDate = new Date(selectedDay.date);
      const dayDate = new Date(day.date);
      return dayDate >= selectedDate;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort ascending by date

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        {/* Close Button */}
        <button className="modal-close-button" onClick={onClose}>
          ×
        </button>

        {/* Modal Title */}
        <h2 className="modal-title">Book an Appointment</h2>

        {/* Lawyer Information */}
        <div className="modal-lawyer-info">
          {lawyer.profile_photo_url ? (
            <img
              src={lawyer.profile_photo_url}
              alt={`${lawyer.name}'s profile`}
              className="modal-profile-photo"
            />
          ) : (
            <div className="modal-placeholder-photo">
              {lawyer.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="modal-lawyer-inner-info">
            <h3 className="modal-lawyer-inner-name">{lawyer.name}</h3>
            <div className="modal-icon-line-div">
              <img src={StarFill} className="modal-lawyer-star-icon" />
              <p className="modal-lawyer-inner-rating">
                {lawyer.average_rating} - {lawyer.number_of_reviews} reviews
              </p>
            </div>
            <div className="modal-icon-line-div">
              <img src={FirmIcon} className="modal-lawyer-practice-icon" />
              <p className="modal-lawyer-inner-firm">{lawyer.firm_name}</p>
            </div>
            <div className="modal-icon-line-div">
              <img src={LocationIcon} className="modal-lawyer-practice-icon" />
              <p className="modal-lawyer-inner-location">
                {lawyer.city}, {lawyer.state} {lawyer.zip_code}
              </p>
            </div>
            <div className="modal-icon-line-div">
              <img src={PracticeIcon} className="modal-lawyer-practice-icon" />
              <p className="modal-lawyer-inner-location">
                {lawyer.practice_areas
                  .map((area) => getPracticeAreaLabel(area))
                  .join(", ")}
              </p>
            </div>
          </div>
        </div>

        {/* Consultation & Date Information */}
        <div className="modal-consultation-info">
          <p className="modal-available-day-title">
            Consultation Type: {consultation.appointment_name}
          </p>
        </div>

        <hr className="h-rule" />

        {/* Available Time Slots */}
        <div className="modal-available-slots">
          <h3 className="modal-available-title">Available Time Slots:</h3>
          {filteredAvailability.length === 0 && !loading && (
            <p className="modal-available-title">
              No availability from the selected date onward.
            </p>
          )}
          {filteredAvailability.map((day, index) => {
            // const oneHourSlots = day.slots.flatMap((slot) =>
            //   splitSlotIntoHours(slot.start_time, slot.end_time)
            // );

            const oneHourSlots = day.slots.flatMap((slot) =>
              splitSlotByDuration(
                slot.start_time,
                slot.end_time,
                consultation.appointment_duration
              )
            );

            return (
              <div key={index} className="modal-availability-day">
                <h4 className="modal-available-day-title">
                  {day.day}, {new Date(day.date).toLocaleDateString()}
                </h4>
                {oneHourSlots.length > 0 ? (
                  <div className="modal-slot-grid">
                    {oneHourSlots.map((slot, idx) => (
                      <button
                        key={idx}
                        className="modal-slot-button"
                        onClick={() => handleSlotSelect(slot, day)}
                      >
                        {formatTime(slot.startTime)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="no-availability-message">No slots available.</p>
                )}
              </div>
            );
          })}
          {loading && (
            <div className="modal-loading">
              <div className="spinner"></div>
            </div>
          )}
          {error && <p className="modal-error">{error}</p>}
        </div>

        {/* Load More Appointments Button */}
        <button
          className="modal-load-more"
          onClick={loadMoreAppointments}
          disabled={loading}
        >
          {loading ? "Loading..." : "Load More Appointments"}
        </button>
      </div>
    </>
  );
};

export default AppointmentModal;
